<template>
  <v-card-title v-if="!changes.length">
    {{ $t('alert.details.audit.no_records') }}
  </v-card-title>
  <v-card-text v-else>
    <v-row>
      <v-col cols="2">
        <vue-slider
          :disabled="changes.length <= 1"
          v-model="num_of_change"
          direction="ttb"
          :tooltip="'always'"
          :tooltip-placement="'right'"
          :data="changes"
          :data-value="'id'"
          style="display: inline-block; margin: 30px 0;"
          :style="{ height: `${(35 * changes.length)}px` }"
        >
          <template v-slot:tooltip="{ value, focus }">
            <div
              :class="['tooltip-inner', { focus }]"
              style="min-width: 120px;"
            >
              <DateFormat
                :date="currentChange.before.updated_date"
                disabled
                :showtime="true"
              />
            </div>
          </template>
        </vue-slider>
        <br>
        <v-btn
          text
          color="primary"
          v-if="is_more"
          :loading="auditLoading"
          @click.prevent="moreAudits"
        >
          {{ $t('alert.details.buttons.more_audit') }} ({{ total - (page + 1) * limit }}+)
        </v-btn>
      </v-col>
      <v-col
        cols="10"
      >
        <v-row>
          <v-col>
            <v-card-subtitle class="pl-0">
              {{ $t('alert.details.audit.previous') }}
              <br>
              <DateFormat
                :date="currentChange.before.updated_date"
                :showtime="true"
              />
            </v-card-subtitle>
          </v-col>
          <v-col cols="1">
  &nbsp;
          </v-col>
          <v-col>
            <v-card-subtitle class="pl-0">
              {{ $t('alert.details.audit.new') }}
              <br>
              <DateFormat
                :date="currentChange.after.updated_date"
                :showtime="true"
              />
            </v-card-subtitle>
          </v-col>
        </v-row>
        <div class="font-weight-light">
          {{ $t('alert.details.form.hit_status.label') }}
        </div>
        <v-card-text class="grey lighten-3 rounded">
          <v-row>
            <v-col>
              <span
                v-for="(hit_status, key) in currentChange.hit_status.filter(x => !x.added)"
                :key="key"
                :style="hit_status.removed ? 'color: #F44336': ''"
              >
                {{ hit_status.value }}
              </span>
            </v-col>
            <v-col
              class="align-self-center text-center"
              cols="1"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-col>
            <v-col>
              <span
                v-for="(hit_status, key) in currentChange.hit_status.filter(x => !x.removed)"
                :key="key"
                :style="hit_status.added ? 'color: #4CAF50': ''"
              >
                {{ hit_status.value }}
              </span>
            </v-col>
          </v-row>
        </v-card-text>
        <br>
        <div class="font-weight-light">
          {{ $t('alert.details.form.exposition.label') }}
        </div>
        <v-card-text class="grey lighten-3 rounded">
          <v-row>
            <v-col>
              <span
                v-for="(exposition, key) in currentChange.exposition.filter(x => !x.added)"
                :key="key"
                :style="exposition.removed ? 'color: #F44336': ''"
              >
                {{ exposition.value }}
              </span>
            </v-col>
            <v-col
              class="align-self-center text-center"
              cols="1"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-col>
            <v-col>
              <span
                v-for="(exposition, key) in currentChange.exposition.filter(x => !x.removed)"
                :key="key"
                :style="exposition.added ? 'color: #4CAF50': ''"
              >
                {{ exposition.value }}
              </span>
            </v-col>
          </v-row>
        </v-card-text>
        <br>
        <div class="font-weight-light">
          {{ $t('alert.details.form.remarks.label') }}
        </div>
        <v-card-text class="grey lighten-3 rounded">
          <v-row>
            <v-col>
              <TextareaSafeDisplay
                v-for="(remarks, key) in currentChange.remarks.filter(x => !x.added)"
                :key="key"
                :style="remarks.removed ? 'color: #F44336': ''"
                :value="remarks.value"
                nl2br="⏎<br>"
              />
            </v-col>
            <v-col
              class="align-self-center text-center"
              cols="1"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-col>
            <v-col>
              <TextareaSafeDisplay
                v-for="(remarks, key) in currentChange.remarks.filter(x => !x.removed)"
                :key="key"
                :style="remarks.added ? 'color: #4CAF50': ''"
                :value="remarks.value"
                nl2br="⏎<br>"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <br>
        <div class="font-weight-light">
          {{ $t('alert_case_details.updatedBy') }}
        </div>
        <v-card-text class="grey lighten-3 rounded">
          <v-row>
            <v-col>
              <span
                v-for="(updated_by, key) in currentChange.updated_by.filter(x => !x.added)"
                :key="key"
                :style="updated_by.removed ? 'color: #F44336': ''"
              >
                {{ updated_by.value }}
              </span>
            </v-col>
            <v-col
              class="align-self-center text-center"
              cols="1"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-col>
            <v-col>
              <span
                v-for="(updated_by, key) in currentChange.updated_by.filter(x => !x.removed)"
                :key="key"
                :style="updated_by.added ? 'color: #4CAF50': ''"
              >
                {{ updated_by.value }}
              </span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-col>
    </v-row>
  </v-card-text>
</template>

<script>
import VueSlider from 'vue-slider-component'
import DateFormat from '@/components/common/DateFormat'
import TextareaSafeDisplay from '@/components/common/TextareaSafeDisplay'
import { mapActions, mapGetters, mapState } from 'vuex'
import * as JSDiff from 'diff'

export default {
  components: {
    VueSlider,
    DateFormat,
    TextareaSafeDisplay
  },
  props: {
    trails: {
      type: Array,
      default: () => []
    },
    alertCase: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    ...mapGetters('alerts', [
      'hit_status_options',
      'suggested_remarks',
      'exposition_options'
    ]),
    ...mapState('alert_case', {
      auditLoading: state => state.audit.requestLoading,
      is_more: state => state.audit.is_more,
      total: state => state.audit.total,
      limit: state => state.audit.limit,
      page: state => state.audit.page
      // options: state => state.listing.options
    }),
    changes () {
      // skip if no current alert case entity records
      if (!this.alertCase) return []
      // sort by id
      const trails = structuredClone(this.trails).sort((a, b) => a.id - b.id)
      // push current alert case entity records to trails
      trails.push({ id: 0, archive_object: this.alertCase, updated_date: '' })

      return trails.reduce((_changes, trail, index) => {
        if (_changes.length === 0) {
          _changes.push({
            id: trail.id,
            before: null,
            after: trail,
            hit_status: null,
            remarks: null,
            updated_by: null
          })
          return _changes
        }
        const _previous_change = _changes[index - 1]
        // console.log(_previous_change.after, trail, index)
        _changes.push({
          id: trail.id,
          before: _previous_change.after,
          after: trail,
          hit_status: JSDiff.diffWordsWithSpace(
            this.hit_status_to_word(_previous_change.after.archive_object),
            this.hit_status_to_word(trail.archive_object)
          ),
          exposition: JSDiff.diffWordsWithSpace(
            this.expoistion_to_word(_previous_change.after.archive_object),
            this.expoistion_to_word(trail.archive_object)
          ),
          remarks: JSDiff.diffWordsWithSpace(
            _previous_change.after.archive_object.remarks,
            trail.archive_object.remarks
          ),
          updated_by: JSDiff.diffWordsWithSpace(
            _previous_change.after.archive_object.updated_by,
            trail.archive_object.updated_by
          )
        })
        this.num_of_change = trail.id

        return _changes
      }, []).filter(change => change.before !== null)
    },
    currentChange () {
      return this.changes.find(change => change.id === this.num_of_change)
    }
  },
  data () {
    return {
      num_of_change: 0
    }
  },
  methods: {
    ...mapActions('alert_case', [
      'moreAudits'
    ]),
    hit_status_to_word (alertCase) {
      return alertCase ? (this.hit_status_options.find(option => option.value === alertCase.status) || { text: '' }).text : ''
    },
    expoistion_to_word (alertCase) {
      return alertCase ? (this.exposition_options(alertCase.status).find(option => String(option.value) === String(alertCase.exposition)) || { text: '' }).text : ''
    }
  }
}

</script>
